import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'Who is Mark Minervini?',
    a: 'A self-taught American stock trader who started in 1983 as a teenager, lost his entire account in the late 1980s, then spent years studying the market\u2019s biggest historical winners before building his SEPA method. He won the U.S. Investing Championship in 1997 with a 155% return and again in 2021 with a 334.8% return, was profiled in Jack Schwager\u2019s Stock Market Wizards, and wrote Trade Like a Stock Market Wizard (2013) and Think & Trade Like a Champion (2017).',
  },
  {
    q: 'What does SEPA stand for?',
    a: 'Specific Entry Point Analysis. It is Minervini\u2019s trademarked name for his whole system: a Stage-2 uptrend confirmed by the Trend Template, accelerating earnings and sales, a catalyst, a low-risk entry from a Volatility Contraction Pattern, and a tight stop-loss.',
  },
  {
    q: 'What are the eight Trend Template rules?',
    a: 'Price above the 150- and 200-day moving averages; the 150-day above the 200-day; the 200-day rising for at least one month; the 50-day above both the 150- and 200-day; price above the 50-day; price at least 30% above its 52-week low; price within 25% of its 52-week high; and a relative-strength rank of 70 or higher.',
  },
  {
    q: 'Why is it called SEPA Lite on DiviDrip?',
    a: 'Because DiviDrip automates only the parts that can be measured honestly: all eight Trend Template rules, a relative-strength rank across every non-dividend common stock, and latest-quarter EPS growth of at least 20% year over year (with a note when that growth rate accelerated). The catalyst, the exact entry and the stop are judgment calls that stay with you.',
  },
  {
    q: 'Can the screener detect a Volatility Contraction Pattern?',
    a: 'No scanner can certify a VCP, because counting contractions and judging their shape is chart reading. DiviDrip shows a Tight base badge as a proxy when the last 10 days\u2019 price range is at most 55% of the 50-day range and 10-day volume is at most 80% of the 50-day average. It means look at the chart, not buy.',
  },
  {
    q: 'Does SEPA exclude dividend stocks?',
    a: 'No. The Trend Template is purely technical, so a dividend payer in a Stage 2 uptrend with strong relative strength is a perfectly valid SEPA candidate. Minervini\'s typical winners are young, fast-growing companies that reinvest cash instead of paying it out, so most SEPA leaders happen to be no- or low-yield. That is a statistical tendency, not a rule. DiviDrip scans non-dividend common stocks because that is where those names cluster, so the preset appears in the Non-Dividend and All modes. ETFs are excluded because baskets never form the supply contraction a VCP depends on.',
  },
  {
    q: 'When does the list update?',
    a: 'A nightly job runs after the U.S. close on weekdays, pulls about 13 months of daily prices plus eight quarters of earnings for every non-dividend common stock, recomputes every rule and rewrites the relative-strength ranks. Weekend and holiday lists reflect the last trading session.',
  },
  {
    q: 'Does passing the screen mean I should buy?',
    a: 'No. Minervini himself treats the template as a filter that removes 90% of the market, not as a buy signal. The stocks that remain still need a catalyst, a proper base, an entry near the pivot and a stop you can live with. Position sizing and risk control are the parts of his record most people skip.',
  },
];

const RULES = [
  ['1', 'Price is above both the 150-day and 200-day moving averages', 'Long-term trend is up'],
  ['2', 'The 150-day average is above the 200-day average', 'Intermediate trend leads the long trend'],
  ['3', 'The 200-day average has been rising for at least one month', 'Not a bounce inside a downtrend'],
  ['4', 'The 50-day average is above the 150- and 200-day averages', 'Short trend leads both'],
  ['5', 'Price is above the 50-day average', 'Stock is not resting on support'],
  ['6', 'Price is at least 30% above its 52-week low', 'The Stage-2 advance has already begun'],
  ['7', 'Price is within 25% of its 52-week high', 'Leaders trade near highs, not at bargains'],
  ['8', 'Relative-strength rank is 70 or better', 'Outperforming at least 70% of the universe'],
];

export default function LearnMinerviniSepa() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };

  return (
    <LearnPage
      title="Minervini SEPA Lite — The Trend Template, the VCP and What a Screener Can Honestly Automate | TwylightCrow"
      description="Mark Minervini's SEPA method explained: the eight-rule Trend Template, the Volatility Contraction Pattern, why most SEPA leaders are low- or no-yield growth names, and exactly how DiviDrip's nightly Minervini SEPA Lite preset applies it."
      canonical="https://twylightcrow.com/learn/minervini-sepa"
      guidePath="/learn/minervini-sepa"
      sectionLabel="Growth Strategy"
      sectionAnchor="non-div-growth-strategy"
      kicker="Learn · Non-Dividend Stocks"
      h1="Minervini SEPA Lite — Trading the Trend Template Without the Guesswork"
      datePublished="2026-09-07"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/spotting-micro-cap-giants', label: 'Spotting Micro-Cap Giants — the other Non-Dividend strategy preset' },
        { to: '/learn/beneish-altman-forensic-safety', label: 'Beneish & Altman — run a forensic check on anything that passes' },
        { to: '/glossary#entry-sepa-specific-entry-point-analysis', label: 'Glossary: SEPA (Specific Entry Point Analysis)' },
        { to: '/glossary#entry-trend-template-minervini', label: 'Glossary: Trend Template (Minervini)' },
        { to: '/glossary#entry-vcp-volatility-contraction-pattern', label: 'Glossary: VCP (Volatility Contraction Pattern)' },
      ]}
    >
      <p>
        In 1983 a teenager with a few thousand dollars bought his first stock and made
        money on it. He concluded investing was easy. By the end of the decade he had
        no system, no risk control and no account left. Mark Minervini&rsquo;s response
        to losing everything was unusual: he stopped trading and spent years reading
        the market&rsquo;s history &mdash; Jesse Livermore, Stan Weinstein&rsquo;s stage
        analysis, Richard Love&rsquo;s <em>Superperformance Stocks</em> &mdash; and
        cataloguing what the biggest winners of every era had in common{' '}
        <em>before</em> they became big winners.
      </p>
      <p>
        The result was <AbbrTip term="SEPA">SEPA</AbbrTip>, Specific Entry Point
        Analysis, and a track record that made it hard to argue with: a 220% average
        annual return across five and a half years of audited results with a single
        losing quarter, a U.S. Investing Championship win in 1997 (+155%), a profile in
        Jack Schwager&rsquo;s <em>Stock Market Wizards</em>, and a second championship
        in 2021 with a verified 334.8%. Two books &mdash; <em>Trade Like a Stock Market
        Wizard</em> (2013) and <em>Think &amp; Trade Like a Champion</em> (2017) &mdash;
        laid the method out in full.
      </p>
      <p>
        This guide explains the method, then is honest about which parts a screener can
        automate. DiviDrip&rsquo;s <strong>Minervini SEPA Lite</strong> preset does the
        measurable half every night; the judgment half stays with you.
      </p>

      <h2>The five pillars of SEPA</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Pillar</th><th>What Minervini looks for</th><th>Automated on DiviDrip?</th></tr>
        </thead>
        <tbody>
          <tr><td className="font-semibold">Trend</td><td>A confirmed Stage-2 uptrend via the eight-rule <AbbrTip term="Trend Template">Trend Template</AbbrTip>.</td><td className="font-mono">Yes &mdash; all 8 rules</td></tr>
          <tr><td className="font-semibold">Fundamentals</td><td>Accelerating quarterly earnings and sales, expanding margins.</td><td className="font-mono">Partly &mdash; EPS YoY &ge;20%, acceleration noted</td></tr>
          <tr><td className="font-semibold">Catalyst</td><td>A reason the stock is moving: new product, new management, earnings surprise.</td><td className="font-mono">No &mdash; read the news</td></tr>
          <tr><td className="font-semibold">Entry point</td><td>A low-risk pivot out of a <AbbrTip term="VCP">VCP</AbbrTip> base.</td><td className="font-mono">Proxy only &mdash; Tight base badge</td></tr>
          <tr><td className="font-semibold">Risk</td><td>A tight stop, typically 5&ndash;8% below entry, sized so a loss is survivable.</td><td className="font-mono">No &mdash; your rules</td></tr>
        </tbody>
      </table>

      <h2>The Trend Template: eight rules, all required</h2>
      <p>
        Minervini estimates the template alone eliminates roughly 90% of listed
        stocks. It is deliberately strict: a stock that fails one rule is out. The
        logic is Weinstein&rsquo;s stage analysis made mechanical &mdash; only buy
        stocks that are already in the mark-up phase, with every moving average
        stacked in the right order and pointing up.
      </p>
      <table className="learn-table">
        <thead><tr><th>#</th><th>Rule</th><th>Why it matters</th></tr></thead>
        <tbody>
          {RULES.map(([n, rule, why]) => (
            <tr key={n}><td className="font-mono">{n}</td><td>{rule}</td><td>{why}</td></tr>
          ))}
        </tbody>
      </table>
      <p>
        Rule 8 needs a universe to rank against. DiviDrip uses the IBD-style
        weighting &mdash; the 3-month return counted twice, plus the 6-, 9- and
        12-month returns &mdash; and ranks that score across every non-dividend common
        stock it scans, from 1 to 99. That number is the <strong>RS</strong> badge on
        each screener row, and the preset sorts by it.
      </p>

      <h2>The VCP: what it is and why no scanner can certify one</h2>
      <p>
        A <AbbrTip term="VCP">Volatility Contraction Pattern</AbbrTip> is the base a
        leader builds before its next leg. Picture two to four pullbacks, each
        shallower than the one before &mdash; 25%, then 12%, then 5% &mdash; with
        volume drying up toward the right edge. Sellers are exhausted; the shares that
        are going to be sold have been sold. The breakout above the final pivot, on a
        surge of volume, is the SEPA entry.
      </p>
      <p>
        Counting those contractions and judging whether they are &ldquo;progressively
        shallower&rdquo; is chart reading, and every serious tool that claims a VCP
        scanner is really measuring tightness. DiviDrip does the same and says so: a
        row gets a <strong>Tight base</strong> badge when its last 10 days&rsquo; price
        range is at most 55% of the 50-day range <em>and</em> 10-day average volume is
        at most 80% of the 50-day average. That is a candidate flag &mdash; open the
        chart, count the contractions yourself, find the pivot.
      </p>

      <h2>Why it lives in Non-Dividend (and why that is not a rule)</h2>
      <p>
        Nothing in the Trend Template mentions dividends &mdash; all eight rules are
        price, moving-average and relative-strength tests, so a dividend payer in a
        Stage 2 uptrend with strong RS is a perfectly valid SEPA candidate. Minervini&rsquo;s
        <em> typical</em> winners, though, are young, fast-growing companies that reinvest
        cash instead of paying it out, so most SEPA leaders happen to be no- or low-yield.
        That is a statistical tendency, not a requirement. ETFs are a different story:
        they are baskets that create and redeem shares on demand, so the supply
        contraction a VCP depends on never forms. DiviDrip scans non-dividend common
        stocks because that is where the method&rsquo;s superperformance names cluster,
        which is why the preset lives in the Non-Dividend and All modes.
      </p>

      <h2>How DiviDrip runs Minervini SEPA Lite</h2>
      <ul>
        <li><strong>Universe:</strong> every non-dividend common stock in the database (no ETFs, funds, warrants or SPAC shells).</li>
        <li><strong>Nightly, after the close:</strong> about 13 months of daily prices and eight quarters of earnings are pulled for each name; the eight rules, the relative-strength rank and the tightness proxy are recomputed and stored.</li>
        <li><strong>To appear in the list:</strong> all eight Trend Template rules must pass <em>and</em> latest-quarter EPS must be up at least 20% year over year. The RS badge tooltip shows the EPS growth and whether it accelerated versus the prior quarter.</li>
        <li><strong>Not required, just flagged:</strong> the Tight base badge.</li>
        <li><strong>Off-days:</strong> weekends and market holidays show the last session&rsquo;s results.</li>
      </ul>
      <p>
        <Link to="/screener?filter=non_dividend&preset=minervini_sepa_lite" className="ht-inline-link font-semibold" data-testid="minervini-guide-open-screener">Open today&rsquo;s Minervini SEPA Lite list &rarr;</Link>{' '}
        &mdash; or open the <Link to="/screener" className="ht-inline-link">Screener</Link> yourself, switch to
        Non-Dividend or All, and pick <strong>Minervini SEPA Lite</strong> from the
        Strategies menu. A two-line explainer appears above the table while it is on.
      </p>

      <h2>What to do with the list</h2>
      <ol>
        <li>Start at the top: highest RS first. Minervini wants leaders, not laggards catching up.</li>
        <li>Open the chart on anything with a Tight base badge and count the contractions. Two to four, each shallower, volume fading on the right. If it is not there, move on.</li>
        <li>Find the catalyst. If you cannot say in one sentence why institutions want this stock, you do not have a SEPA setup.</li>
        <li>Run the forensic check. The <Link to="/learn/beneish-altman-forensic-safety" className="ht-inline-link">Beneish and Altman panel</Link> catches the accounting problems a momentum screen cannot see.</li>
        <li>Decide the stop before the entry. Minervini&rsquo;s edge was never the win rate; it was cutting losses at 5&ndash;8% while letting winners run.</li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>
    </LearnPage>
  );
}
